import React, { useState, useRef, useEffect } from 'react';

export interface AudioPlayerProps {
  audioUrl: string;
  title?: string;
  onEnded?: () => void;
  onError?: (error: string) => void;
  showWaveform?: boolean;
  showDownload?: boolean;
  onDownload?: () => void;
  autoPlay?: boolean;
  className?: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  audioUrl,
  title = 'Audio Recording',
  onEnded,
  onError,
  showWaveform = true,
  showDownload = true,
  onDownload,
  autoPlay = false,
  className = ''
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const audioRef = useRef<HTMLAudioElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Initialize audio
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.playbackRate = playbackRate;
      audioRef.current.muted = isMuted;

      if (autoPlay) {
        playAudio();
      }
    }
  }, [audioUrl]);

  // Generate waveform when audio loads
  useEffect(() => {
    if (showWaveform && audioUrl) {
      generateWaveform();
    }
  }, [audioUrl, showWaveform]);

  // Update canvas on time change
  useEffect(() => {
    if (showWaveform && canvasRef.current) {
      drawWaveform();
    }
  }, [currentTime, waveformData, isPlaying]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const generateWaveform = async () => {
    try {
      const response = await fetch(audioUrl);
      const arrayBuffer = await response.arrayBuffer();
      const audioContext = new AudioContext();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      const rawData = audioBuffer.getChannelData(0); // Get first channel
      const samples = 100; // Number of bars to display
      const blockSize = Math.floor(rawData.length / samples);
      const filteredData: number[] = [];

      for (let i = 0; i < samples; i++) {
        const start = blockSize * i;
        let sum = 0;
        for (let j = 0; j < blockSize; j++) {
          sum += Math.abs(rawData[start + j]);
        }
        filteredData.push(sum / blockSize);
      }

      // Normalize
      const max = Math.max(...filteredData);
      const normalized = filteredData.map(val => val / max);
      setWaveformData(normalized);

      audioContext.close();
    } catch (error) {
      console.error('Failed to generate waveform:', error);
      onError?.('Failed to load audio waveform');
    }
  };

  const drawWaveform = () => {
    const canvas = canvasRef.current;
    if (!canvas || waveformData.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const barWidth = width / waveformData.length;
    const progress = duration > 0 ? currentTime / duration : 0;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw waveform bars
    waveformData.forEach((value, index) => {
      const barHeight = value * height * 0.8;
      const x = index * barWidth;
      const y = (height - barHeight) / 2;

      // Color based on progress
      const barProgress = index / waveformData.length;
      const isPlayed = barProgress <= progress;

      ctx.fillStyle = isPlayed ? '#D4A017' : '#4b5563';
      ctx.fillRect(x, y, barWidth - 1, barHeight);
    });

    // Draw progress indicator
    if (isPlaying) {
      ctx.fillStyle = '#D4A017';
      ctx.fillRect(progress * width - 1, 0, 2, height);
    }
  };

  const playAudio = () => {
    if (audioRef.current) {
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          updateProgress();
        })
        .catch(error => {
          console.error('Playback error:', error);
          onError?.('Failed to play audio');
        });
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      pauseAudio();
    } else {
      playAudio();
    }
  };

  const updateProgress = () => {
    if (audioRef.current && isPlaying) {
      setCurrentTime(audioRef.current.currentTime);
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setIsLoading(false);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    onEnded?.();
  };

  const handleSeek = (e: React.MouseEvent<HTMLElement>) => {
    if (!audioRef.current || !progressBarRef.current) return;

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
    }
  };

  const handlePlaybackRateChange = (rate: number) => {
    setPlaybackRate(rate);
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
  };

  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
    }
  };

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 10);
    }
  };

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={`audio-player ${className}`} style={{
      background: '#1f2937',
      borderRadius: '0.75rem',
      padding: '1rem',
      border: '1px solid #374151'
    }}>
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={() => onError?.('Failed to load audio file')}
      />

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.75rem'
      }}>
        <h4 style={{ margin: 0, color: '#f9fafb', fontSize: '0.875rem' }}>
          {title}
        </h4>
        {showDownload && (
          <button
            onClick={onDownload}
            style={{
              background: 'transparent',
              border: '1px solid #4b5563',
              color: '#d1d5db',
              padding: '0.25rem 0.5rem',
              borderRadius: '0.375rem',
              fontSize: '0.75rem',
              cursor: 'pointer'
            }}
            title="Download audio"
          >
            ⬇️ Download
          </button>
        )}
      </div>

      {/* Waveform */}
      {showWaveform && (
        <div style={{ marginBottom: '0.75rem' }}>
          <canvas
            ref={canvasRef}
            width={600}
            height={80}
            style={{
              width: '100%',
              height: '80px',
              background: '#111827',
              borderRadius: '0.375rem',
              cursor: 'pointer'
            }}
            onClick={handleSeek}
          />
        </div>
      )}

      {/* Progress Bar */}
      <div
        ref={progressBarRef}
        onClick={handleSeek}
        style={{
          position: 'relative',
          width: '100%',
          height: '6px',
          background: '#374151',
          borderRadius: '3px',
          cursor: 'pointer',
          marginBottom: '0.75rem'
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: `${progress}%`,
            background: '#D4A017',
            borderRadius: '3px',
            transition: 'width 0.1s linear'
          }}
        />
      </div>

      {/* Time Display */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '0.75rem',
        color: '#d1d5db',
        marginBottom: '0.75rem'
      }}>
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>

      {/* Controls */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.5rem'
      }}>
        {/* Playback Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={skipBackward}
            disabled={isLoading}
            style={{
              background: '#374151',
              border: 'none',
              color: '#f9fafb',
              padding: '0.5rem',
              borderRadius: '0.375rem',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '0.875rem',
              opacity: isLoading ? 0.5 : 1
            }}
            title="Skip backward 10s"
          >
            ⏪
          </button>

          <button
            onClick={togglePlayPause}
            disabled={isLoading}
            style={{
              background: '#D4A017',
              border: 'none',
              color: '#1f2937',
              padding: '0.75rem 1rem',
              borderRadius: '0.5rem',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              opacity: isLoading ? 0.5 : 1
            }}
          >
            {isLoading ? '⏳' : isPlaying ? '⏸️' : '▶️'}
          </button>

          <button
            onClick={skipForward}
            disabled={isLoading}
            style={{
              background: '#374151',
              border: 'none',
              color: '#f9fafb',
              padding: '0.5rem',
              borderRadius: '0.375rem',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '0.875rem',
              opacity: isLoading ? 0.5 : 1
            }}
            title="Skip forward 10s"
          >
            ⏩
          </button>
        </div>

        {/* Speed Controls */}
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          {[0.5, 1, 1.5, 2].map(rate => (
            <button
              key={rate}
              onClick={() => handlePlaybackRateChange(rate)}
              style={{
                background: playbackRate === rate ? '#D4A017' : '#374151',
                border: 'none',
                color: playbackRate === rate ? '#1f2937' : '#f9fafb',
                padding: '0.375rem 0.5rem',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: playbackRate === rate ? 'bold' : 'normal'
              }}
              title={`${rate}x speed`}
            >
              {rate}x
            </button>
          ))}
        </div>

        {/* Volume Controls */}
        <div
          style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
          onMouseEnter={() => setShowVolumeSlider(true)}
          onMouseLeave={() => setShowVolumeSlider(false)}
        >
          <button
            onClick={toggleMute}
            style={{
              background: '#374151',
              border: 'none',
              color: '#f9fafb',
              padding: '0.5rem',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted || volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊'}
          </button>

          {showVolumeSlider && (
            <div style={{
              position: 'absolute',
              bottom: '100%',
              right: 0,
              background: '#374151',
              padding: '0.75rem 0.5rem',
              borderRadius: '0.5rem',
              marginBottom: '0.5rem',
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
            }}>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={handleVolumeChange}
                style={{
                  width: '80px',
                  transform: 'rotate(-90deg)',
                  transformOrigin: 'center',
                  margin: '30px -20px'
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Loading indicator */}
      {isLoading && (
        <div style={{
          marginTop: '0.5rem',
          fontSize: '0.75rem',
          color: '#d1d5db',
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          Loading audio...
        </div>
      )}
    </div>
  );
};

export default AudioPlayer;
